import { IterableMap } from "@redotech/react-util/component";
import classNames from "classnames";
import { JSXElementConstructor, memo, ReactNode } from "react";
import { ButtonSize, IconButton } from "../../button";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { SpacingValue } from "../../theme/box";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import * as lightModeCss from "../../theme/component-colors-light.module.css";
import { TextSizeValue } from "../../theme/typography";
import { OptionalTooltip } from "../../tooltip/optional-tooltip";
import { UserImage, UserImageSize } from "../../user-image";
import * as badgeColorCss from "./redo-badge-colors.module.css";
import * as badgeCss from "./redo-badge.module.css";

export const redoBadgeSize = ["xs", "sm", "md", "lg"] as const;
export type RedoBadgeSize = (typeof redoBadgeSize)[number];

export const redoBadgeColor = [
  "gray",
  "inactive",
  "brand",
  "error",
  "warning",
  "success",
  "grayBlue",
  "blueLight",
  "blue",
  "indigo",
  "purple",
  "pink",
  "orange",
  "black",
  "white",
  "whiteSuccess",
] as const;
export type RedoBadgeColor = (typeof redoBadgeColor)[number];

export const redoBadgeType = ["primary", "alternate"] as const;
export type RedoBadgeType = (typeof redoBadgeType)[number];

type BadgeSegment = ColorSwatch | BadgeIcon;
export interface BadgeIcon {
  type: "icon";
  /** If not provided, the icon's size will match its badge. */
  size?: RedoBadgeSize;
  Icon: JSXElementConstructor<any>;
  onClick?: () => void;
  /** Optional tooltip text to display on hover */
  tooltip?: string;
  /** Accessible label for the icon button (icon-only buttons need a name). */
  ariaLabel?: string;
}
interface ColorSwatch {
  type: "color";
  color: string;
}

/**
 * @param type if you are doing something other than pill color, it needs to be fleshed out in the colors module.
 */
export interface RedoBadgeProps {
  segmentLeading?: BadgeSegment;
  iconTrailing?: BadgeSegment | BadgeSegment[];
  avatar?: { alt: string; name?: string; imageUrl: string | null } | undefined;
  text?: string | ReactNode;
  size?: RedoBadgeSize;
  type?: RedoBadgeType;
  color?: RedoBadgeColor;
  iconColor?: RedoBadgeColor;
  setRef?: (ref: HTMLElement | null) => void;
  shadow?: boolean;
  className?: string;
  textClassName?: string;
  dataTestId?: string;
  truncateAt?: number;
  uiSelector?: string;
  monospace?: boolean;
  darkMode?: boolean;
}

function truncateText(
  text: string | ReactNode,
  truncateAt?: number,
): string | ReactNode {
  if (typeof text !== "string" || !truncateAt) {
    return text;
  }
  if (text.length <= truncateAt) {
    return text;
  }
  return text.slice(0, truncateAt - 3) + "...";
}

export const RedoBadge = memo(function RedoBadge({
  iconTrailing,
  segmentLeading,
  avatar,
  text,
  size = "sm",
  type = "primary",
  color = "gray",
  setRef,
  iconColor,
  shadow = false,
  className,
  textClassName,
  dataTestId,
  truncateAt,
  uiSelector,
  monospace,
  darkMode = false,
}: RedoBadgeProps) {
  const truncatedText = text ? truncateText(text, truncateAt) : text;
  const px = truncatedText ? regularPaddingX[size] : iconOnlyPaddingX[size];
  const py = truncatedText ? paddingY[size] : iconOnlyPaddingY[size];
  const textSize = fontSize[size];

  const variantClassnames = [
    badgeCss[size],
    badgeColorCss[type],
    badgeColorCss[color],
  ];

  function renderSegment(badge: BadgeSegment) {
    if (badge.type === "icon") {
      const iconWrapperClassNames = classNames(
        badgeColorCss.iconWrapper,
        badgeCss.iconWrapper,
        badgeCss[badge.size ?? size],
      );
      const icon = <badge.Icon />;
      // FIXME icon color does nothing
      const wrappedIcon = badge.onClick ? (
        <IconButton
          aria-label={badge.ariaLabel}
          className={classNames(
            badgeCss.iconButton,
            iconColor && badgeColorCss[iconColor],
          )}
          onClick={badge.onClick}
          size={ButtonSize.SMALL}
        >
          {icon}
        </IconButton>
      ) : (
        icon
      );

      return (
        <OptionalTooltip
          meetsCondition={!!badge.tooltip}
          tooltipProps={{ title: badge.tooltip || "" }}
        >
          <Flex className={iconWrapperClassNames}>{wrappedIcon}</Flex>
        </OptionalTooltip>
      );
    } else if (badge.type === "color") {
      const colorSwatch = badge.color;
      return (
        <div
          className={badgeCss.colorSwatch}
          style={{ backgroundColor: colorSwatch }}
        />
      );
    } else {
      throw new Error("Invalid badge type");
    }
  }

  return (
    <Flex
      align="center"
      className={classNames(
        darkMode ? darkModeCss.darkMode : lightModeCss.lightMode,
        darkMode && badgeColorCss.darkMode,
        badgeCss.arbiterBadgeContainer,
        variantClassnames,
        className,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        { [badgeCss.shadow]: shadow },
      )}
      data-testid={dataTestId}
      data-ui={uiSelector}
      gap={itemGap[size]}
      px={px}
      py={py}
    >
      {segmentLeading && renderSegment(segmentLeading)}
      {avatar && (
        <UserImage
          alt={avatar.alt}
          imageUrl={avatar.imageUrl}
          name={avatar.name}
          size={avatarSize[size]}
        />
      )}
      {truncatedText && (
        <Text
          className={textClassName}
          fontFamily={monospace ? "monospace" : undefined}
          fontSize={textSize}
          fontWeight="medium"
          overflow="hidden"
          ref={setRef}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {truncatedText}
        </Text>
      )}
      {iconTrailing && (
        <IterableMap items={[iconTrailing].flat()}>{renderSegment}</IterableMap>
      )}
    </Flex>
  );
});

const fontSize: Record<RedoBadgeSize, TextSizeValue> = {
  xs: "xxs",
  sm: "xs",
  md: "sm",
  lg: "sm",
};

const iconOnlyPaddingY: Record<RedoBadgeSize, SpacingValue> = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "md",
};

const paddingY: Record<RedoBadgeSize, SpacingValue> = {
  xs: "xxs",
  sm: "xxs",
  md: "xxs",
  lg: "xs",
};

const iconOnlyPaddingX: Record<RedoBadgeSize, SpacingValue> = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "md",
};

const regularPaddingX: Record<RedoBadgeSize, SpacingValue> = {
  xs: "xs",
  sm: "md",
  md: "md",
  lg: "lg",
};

const itemGap: Record<RedoBadgeSize, SpacingValue> = {
  xs: "xxs",
  sm: "xs",
  md: "sm",
  lg: "sm",
};

const avatarSize: Record<RedoBadgeSize, UserImageSize> = {
  xs: UserImageSize.X_TINY,
  sm: UserImageSize.TINY,
  md: UserImageSize.TINY,
  lg: UserImageSize.TINY,
};

/** Compact 8x16 stand-in for a full RedoBadge, tinted with the same color
 * tokens. Use when a row of badges needs to collapse in a narrow container
 * but still convey each badge's color. */
export const RedoCompactBadgeSwatch = memo(function RedoCompactBadgeSwatch({
  color = "gray",
  type = "primary",
}: {
  color?: RedoBadgeColor;
  type?: RedoBadgeType;
}) {
  return (
    <div
      className={classNames(
        lightModeCss.lightMode,
        badgeColorCss[type],
        badgeColorCss[color],
        badgeCss.compactSwatch,
      )}
    />
  );
});
